import Button from 'common/components/Button/Button';
import { Link } from 'react-router-dom';
import useAuth from 'common/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import Status from 'common/components/Status/Status';
import dayjs from 'dayjs';
import { User } from 'types';
import Role from 'common/components/Role/Role';
import WarningIcon from 'components/Icon/icons/WarningIcon';
import Icon from 'components/Icon/Icon';

function UserItem({ item: user, onDelete, onNavigate }: any) {
  const { user: currentUser } = useAuth();

  const { email, role, confirmed, blocked, createdAt } = user as User;
  const canEditItem =
    (role.name !== 'SuperAdmin' || currentUser?.isSuperAdmin) && currentUser?.id !== user.id;

  const openLink = !!user.profiles?.[0]
    ? `/students/${user.id}`
    : !!user.operator
    ? `/mediators/${user.id}`
    : `/users/edit/${user.id}`;

  const { t } = useTranslation();

  return !user ? null : (
    <div className="white-box responsive-flex-lg px-4 py-3 mt-2 w-full relative cursor-pointer transition-all">
      <Link
        to={openLink}
        className={
          !canEditItem
            ? 'pointer-events-none responsive-flex-lg flex-1'
            : 'responsive-flex-lg flex-1'
        }
      >
        <div className="flex flex-col flex-1 justify-between lg:pr-4">
          <div className="flex h-full gap-3">
            <div className="grid w-full gap-1 pt-3 lg:py-0">
              <div className="flex items-center gap-1 mb-1 text-xl">
                {user.profiles?.[0] ? (
                  <span className="font-bold text-primary leading-4">
                    {user.profiles[0].username}
                  </span>
                ) : user.operator ? (
                  <span className="font-bold text-primary leading-4">{user.operator.username}</span>
                ) : (
                  <span className="font-bold text-primary leading-4">{user.username}</span>
                )}

                <div className="text-xs text-right flex-1 px-1 pt-0 pb-4">
                  {t('forms.subscribedThe', {
                    since: dayjs(createdAt).format('DD/MM/YYYY'),
                  })}
                </div>
              </div>

              <div className="flex flex-row w-full items-center justify-between mt-1 self-end place-self-end justify-self-end">
                <div className="flex items-center gap-2">
                  <Icon name="EnvelopeIcon" className="w-4 h-4 mt-0.5" />
                  {email ?? '-'}
                </div>
                {!user.profiles?.[0] && !user.operator && (
                  <div className="flex flex-row items-center gap-2 text-amber-700 italic opacity-60">
                    <WarningIcon className="w-4 h-4 mr-1" />
                    {t('titleLegends.noProfile')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Link>

      <div className="flex flex-col justify-between items-end text-xs lg:border-l pl-4 min-w-[24em] border-slate-150 gap-2">
        <div className={role ? 'flex justify-end gap-2' : ''}>
          {role && <Role userRole={role?.name} />}
          {!confirmed ? (
            <Status status="not-confirmed" />
          ) : (
            <Status status={blocked ? 'blocked' : 'active'} />
          )}
        </div>

        <div className="flex w-full items-center justify-end text-slate-500">
          <div>
            <div className="flex w-full gap-1">
              <Button
                type="error-line"
                icon="TrashIcon"
                compact
                onClick={() => onDelete(user.id)}
                disabled={!canEditItem}
                confirmMessage={t('actions.confirmDelete')}
              />
              <Button
                title={t('actions.edit')}
                type="warning-line"
                icon="PencilSquareIcon"
                onClick={() => onNavigate(user.id)}
                disabled={!canEditItem}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserItem;
